/**
 * Size/variant dropdown on PLP product cards.
 *
 * Every value it writes is pre-rendered in Liquid and read off the clicked
 * option's data attributes, so money formatting and discount maths live in one
 * place (snippets/card-variant-dropdown.liquid + card-variant-ribbon.liquid)
 * and no request is made per card.
 *
 * Sold-out options are rendered disabled, so the Add to Cart button never needs
 * a sold-out state here — only the variant id it submits changes.
 */
if (!customElements.get('card-variant-select')) {
  customElements.define(
    'card-variant-select',
    class CardVariantSelect extends HTMLElement {
      connectedCallback() {
        this.toggle = this.querySelector('[data-variant-toggle]');
        this.panel = this.querySelector('[data-variant-panel]');
        this.label = this.querySelector('[data-variant-toggle-label]');
        this.card = this.closest('.plp-card-wrapper');
        if (!this.toggle || !this.panel || !this.card) return;

        this.onDocumentClick = this.onDocumentClick.bind(this);

        this.toggle.addEventListener('click', () => this.setOpen(!this.isOpen));

        this.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && this.isOpen) {
            this.setOpen(false);
            this.toggle.focus();
          }
        });

        this.panel.addEventListener('click', (event) => {
          const option = event.target.closest('.plp-variant-option');
          if (!option || option.disabled) return;
          this.select(option);
        });
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick, true);
      }

      get isOpen() {
        return this.toggle.getAttribute('aria-expanded') === 'true';
      }

      setOpen(open) {
        this.toggle.setAttribute('aria-expanded', String(open));
        this.panel.hidden = !open;
        this.classList.toggle('is-open', open);

        /* Capture phase so the dismiss tap is seen before it reaches the card's
           title link. Both calls must pass the same flag or the listener leaks. */
        if (open) {
          document.addEventListener('click', this.onDocumentClick, true);
        } else {
          document.removeEventListener('click', this.onDocumentClick, true);
        }
      }

      /**
       * Closes the panel on an outside click, and swallows that click when it
       * landed on this card's title link.
       *
       * snippet-card-product-plp.css stretches that link over the whole card, so
       * without this the one tap that dismisses the panel would also navigate to
       * the PDP. Scoped to the link itself rather than the whole card, so the
       * quick-add button still fires on first tap, and taps on other cards still
       * navigate as normal.
       */
      onDocumentClick(event) {
        if (this.contains(event.target)) return;

        this.setOpen(false);

        const cardLink = event.target.closest('.plp-card__title a');
        if (cardLink && this.card.contains(cardLink)) {
          event.preventDefault();
          event.stopPropagation();
        }
      }

      select(option) {
        this.panel.querySelectorAll('.plp-variant-option').forEach((el) => {
          const isSelected = el === option;
          el.classList.toggle('is-selected', isSelected);
          el.setAttribute('aria-selected', String(isSelected));
        });

        this.label.textContent = option.querySelector('.plp-variant-option__name').textContent;

        this.updateCartInput(option.dataset.variantId);
        this.updatePrice(option.dataset.price, option.dataset.compare);
        this.updateRibbon(option.dataset.ribbon);
        this.updateImage(option.dataset.imageSrc, option.dataset.imageSrcset);

        this.setOpen(false);
        this.toggle.focus();
      }

      /* The quick-add form posts this input, exactly as it did before. */
      updateCartInput(variantId) {
        const input = this.card.querySelector('input[name="id"]');
        if (input && variantId) input.value = variantId;
      }

      /**
       * Mirrors the structure of snippets/price.liquid, where `price--on-sale`
       * decides whether the regular or the sale block is shown.
       *
       * price.liquid only emits `.price__sale` when the variant rendered on the
       * server was discounted. Without that block, adding `price--on-sale` would
       * hide `.price__regular` and leave no price at all, so the class is only
       * ever applied when the sale block actually exists.
       */
      updatePrice(price, compare) {
        const priceRoot = this.card.querySelector('.plp-card__meta .price');
        if (!priceRoot || !price) return;

        const saleBlock = priceRoot.querySelector('.price__sale');
        const onSale = Boolean(compare) && Boolean(saleBlock);

        priceRoot.classList.toggle('price--on-sale', onSale);

        const regular = priceRoot.querySelector('.price__regular .price-item--regular');
        if (regular) regular.textContent = price;

        if (!saleBlock) return;

        const salePrice = saleBlock.querySelector('.price-item--sale');
        if (salePrice) salePrice.textContent = price;

        const saleCompare = saleBlock.querySelector('s.price-item--regular');
        if (saleCompare) saleCompare.textContent = compare || '';
      }

      updateRibbon(ribbon) {
        const wrapper = this.card.querySelector('[data-plp-ribbon]');
        if (!wrapper) return;

        const text = wrapper.querySelector('[data-plp-ribbon-text]');
        if (text) text.innerHTML = ribbon || '';
        wrapper.hidden = !ribbon;
      }

      /* Only swaps when the selected variant has its own image assigned. */
      updateImage(src, srcset) {
        if (!src) return;

        const image = this.card.querySelector('.plp-card__image-wrap img');
        if (!image) return;

        if (srcset) image.srcset = srcset.trim();
        image.src = src;
      }
    }
  );
}
